import React, { PropsWithChildren, ReactNode } from 'react';
import { IconArrowLeft, IconArrowRight } from '@maiia/design-system';
import { Box, Button, Grid } from '@mui/material';
import { styled, css } from '@mui/material/styles';
import Breadcrumbs, {
  BreadcrumbItem,
} from '../../atoms/Breadcumbs/Breadcrumbs';
import PreFooter from '@/components/organisms/PreFooter/PreFooter';
import { useTranslation } from '@/src/i18n';

const PaginationItem = styled(Button)(
  ({ theme }) => css`
    padding-left: 0; // Force links to be aligned with container
    padding-right: 0;
    color: ${theme.palette.text.primary};

    ${theme.breakpoints.down('md')} {
      max-width: 150px; // Fix width to 150px (default min-width for buttons)
    }
  `,
);

const ContentWrapper = styled(Box)(
  ({ theme }) => css`
    max-width: 1480px;
    margin: ${theme.headerHeight}px auto 0 auto;
    padding: ${theme.spacing(4)} ${theme.spacing(3)};

    .pre-footer {
      max-width: 100%;
      margin: ${theme.spacing(6)} auto 0;
    }
  `,
);

type Props = PropsWithChildren<ReactNode> & {
  breadcrumbs?: BreadcrumbItem[];
  showNext: boolean;
  showPrev: boolean;
  onNext?: () => void;
  onPrev?: () => void;
  pagination?: JSX.Element | null;
};

const InventoryPage = ({
  children,
  breadcrumbs,
  showNext,
  showPrev,
  onNext,
  onPrev,
  pagination,
}: Props) => {
  const { t } = useTranslation();

  const showDefaultPagination = React.useMemo(
    () => !pagination && (showPrev || showNext),
    [pagination, showPrev, showNext],
  );

  return (
    <ContentWrapper>
      {children}
      {!showDefaultPagination && pagination}
      {showDefaultPagination && (
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={6}>
            {showPrev && (
              <PaginationItem
                color="info"
                variant="text"
                data-cy="btn-pagination-previous"
                startIcon={<IconArrowLeft aria-hidden="true" />}
                onClick={onPrev}
              >
                {t('previous-results')}
              </PaginationItem>
            )}
          </Grid>
          <Grid item xs={6} sx={{ textAlign: 'right' }}>
            {showNext && (
              <PaginationItem
                color="info"
                variant="text"
                data-cy="btn-pagination-next"
                endIcon={<IconArrowRight aria-hidden="true" />}
                onClick={onNext}
              >
                {t('next-results')}
              </PaginationItem>
            )}
          </Grid>
        </Grid>
      )}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <PreFooter>
          <Breadcrumbs items={breadcrumbs} />
        </PreFooter>
      )}
    </ContentWrapper>
  );
};
export default InventoryPage;
